/**
 * Forms engine — shared contract (docs/FORMS_DESIGN.md §4–§7). One zod model + pure helpers used
 * by the API (validation, publish, public render, submit), the web builder and the public page,
 * so the page and the server always agree on what a field is, when it is shown and what a valid
 * answer looks like.
 *
 * Two kinds share it: `interest` (announcement + interest declaration) and `event` (RSVP).
 */
import { z } from 'zod';
import { EMAIL_FONTS, EMAIL_PALETTE, isAllowedHref } from './emailDesign';
import { looksSensitiveLabel } from './contracts/schema';
import { toMachineKey } from './text';

// ─── vocabulary ──────────────────────────────────────────────────────────────

export const FORM_KINDS = ['interest', 'event'] as const;
export type FormKind = (typeof FORM_KINDS)[number];

export const FORM_STATUSES = ['draft', 'open', 'closed', 'archived'] as const;
export type FormStatus = (typeof FORM_STATUSES)[number];

export const FORM_DEFINITION_VERSION = 1 as const;
export const MAX_FORM_FIELDS = 30;
export const MAX_FIELD_OPTIONS = 20;
export const MAX_PAGE_BLOCKS = 16;
export const MAX_FOOTER_BLOCKS = 4;
/** The builder warns above this many questions (data minimisation, §16). */
export const FORM_FIELDS_WARNING = 8;
export const MAX_GUESTS = 10;

export const INTEREST_STATUSES = ['interested', 'maybe', 'not_interested'] as const;
export type InterestStatus = (typeof INTEREST_STATUSES)[number];
/** `waitlist` is assigned by the server only (v1.1). */
export const EVENT_STATUSES = ['going', 'maybe', 'not_going', 'waitlist'] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];
export const FORM_RESPONSE_STATUSES = [
  'interested',
  'maybe',
  'not_interested',
  'going',
  'not_going',
  'waitlist',
] as const;
export type FormResponseStatus = (typeof FORM_RESPONSE_STATUSES)[number];

/** Statuses a person may choose for a kind (never `waitlist`). */
export function choosableStatuses(kind: FormKind): readonly FormResponseStatus[] {
  return kind === 'interest' ? INTEREST_STATUSES : (['going', 'maybe', 'not_going'] as const);
}

/** Statuses that belong to a kind (stats, audiences, validation). */
export function kindStatuses(kind: FormKind): readonly FormResponseStatus[] {
  return kind === 'interest' ? INTEREST_STATUSES : EVENT_STATUSES;
}

/** Positive answers: «ενδιαφέρεται / ίσως» or «έρχεται / ίσως». */
export function positiveStatuses(kind: FormKind): FormResponseStatus[] {
  return kind === 'interest' ? ['interested', 'maybe'] : ['going', 'maybe'];
}

export const FORM_RESPONSE_SOURCES = ['personal_link', 'share_link', 'staff', 'walk_in'] as const;
export type FormResponseSource = (typeof FORM_RESPONSE_SOURCES)[number];

export const SEAT_STATUSES = ['confirmed', 'pending_approval', 'offered'] as const;
export type SeatStatus = (typeof SEAT_STATUSES)[number];

/**
 * Why a waitlisted party is skipped by automatic seat offers (the owner decides): no channel we may
 * notify them on, or a previous offer expired unanswered.
 */
export const WAITLIST_FLAGS = ['unreachable', 'offer_expired'] as const;
export type WaitlistFlag = (typeof WAITLIST_FLAGS)[number];

/** Seat offers hold the seat this long (shorter when the event is sooner). */
export const SEAT_OFFER_HOLD_HOURS = 24;
/** No automatic offers this close to the event start (the owner decides). */
export const SEAT_OFFER_CUTOFF_HOURS = 6;
/**
 * An offer must leave the person at least this long to answer after the message can go out
 * (quiet hours); otherwise nobody is offered and the party is flagged for the owner to call.
 */
export const SEAT_OFFER_MIN_REPLY_HOURS = 2;

/** Derived audience states (computed, never stored). */
export const FORM_AUDIENCE_STATES = [
  'responded',
  'no_reply',
  'not_reached',
  'not_sent',
  'checked_in',
  'not_checked_in',
] as const;
export type FormAudienceState = (typeof FORM_AUDIENCE_STATES)[number];

export const FORM_CHANNEL_PLANS = ['email_then_sms', 'sms_then_email', 'email', 'sms'] as const;
export type FormChannelPlan = (typeof FORM_CHANNEL_PLANS)[number];

export const SHARE_IDENTITY_MODES = [
  'email',
  'phone',
  'email_or_phone',
  'email_and_phone',
] as const;
export type ShareIdentityMode = (typeof SHARE_IDENTITY_MODES)[number];

export const FORM_FIELD_TYPES = [
  'interest',
  'rsvp',
  'single_choice',
  'multi_choice',
  'rating',
  'short_text',
  'long_text',
  'number',
  'date',
  'consent',
  'crm',
] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

export const CONSENT_FIELD_PURPOSES = [
  'marketing_email',
  'marketing_sms',
  'special_category',
  'custom',
] as const;
export type ConsentFieldPurpose = (typeof CONSENT_FIELD_PURPOSES)[number];

export const CRM_CORE_TARGETS = [
  'full_name',
  'first_name',
  'last_name',
  'email',
  'phone',
  'birth_date',
] as const;
export type CrmCoreTarget = (typeof CRM_CORE_TARGETS)[number];
/** `field:<field definition uuid>` — a custom contact field (Pro `schema.custom`). */
export type CrmTarget = CrmCoreTarget | `field:${string}`;

export const CRM_RULES = ['if_missing', 'if_missing_or_unreachable', 'always'] as const;
export type CrmRule = (typeof CRM_RULES)[number];
export const CRM_WRITE_MODES = ['fill_empty', 'overwrite'] as const;
export type CrmWriteMode = (typeof CRM_WRITE_MODES)[number];

/** Custom field types a CRM-linked form field may target (never reference/object/datetime). */
export const CRM_ELIGIBLE_FIELD_TYPES = [
  'text',
  'long_text',
  'integer',
  'decimal',
  'money',
  'boolean',
  'date',
  'enum',
  'multi_enum',
  'email',
  'phone',
  'url',
] as const;

// ─── ids & keys ──────────────────────────────────────────────────────────────

export const FIELD_ID_RE = /^f_[a-z0-9]{8}$/;
export const FIELD_KEY_RE = /^[a-z][a-z0-9_]{0,47}$/;
export const OPTION_ID_RE = /^o_[a-z0-9]{1,16}$/;
const BLOCK_ID_RE = /^[A-Za-z0-9_-]{1,40}$/;
const CRM_FIELD_TARGET_RE = /^field:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function randomBase36(n: number): string {
  let s = '';
  while (s.length < n) s += Math.random().toString(36).slice(2);
  return s.slice(0, n);
}

/** Stable field id (generated by the builder; immutable). */
export function newFieldId(): string {
  return `f_${randomBase36(8)}`;
}

export function newOptionId(): string {
  return `o_${randomBase36(6)}`;
}

/** Field key from its label at creation (immutable once published: exports, segments). */
export function fieldKeyFromLabel(label: string, taken: Iterable<string> = []): string {
  const used = new Set(taken);
  const base = toMachineKey(label || 'field', 40).replace(/^[^a-z]+/, '') || 'field';
  let key = base;
  for (let i = 2; used.has(key); i++) key = `${base}_${i}`;
  return key.slice(0, 48);
}

export function isCrmTarget(v: string): v is CrmTarget {
  return (CRM_CORE_TARGETS as readonly string[]).includes(v) || CRM_FIELD_TARGET_RE.test(v);
}

/** The custom field definition id of a `field:<id>` target (null for core targets). */
export function crmFieldDefinitionId(target: string): string | null {
  return target.startsWith('field:') ? target.slice(6) : null;
}

// ─── fields ──────────────────────────────────────────────────────────────────

export const FIELD_CONDITION_OPS = ['eq', 'neq', 'in', 'answered', 'not_answered'] as const;
export const FieldConditionSchema = z.object({
  fieldId: z.string().regex(FIELD_ID_RE),
  op: z.enum(FIELD_CONDITION_OPS),
  value: z
    .union([z.string().max(60), z.array(z.string().max(60)).max(MAX_FIELD_OPTIONS)])
    .optional(),
});
export type FieldCondition = z.infer<typeof FieldConditionSchema>;

const LabelText = z.string().trim().min(1).max(200);
const OptionalLabel = z.string().trim().max(120).default('');

const fieldBase = {
  id: z.string().regex(FIELD_ID_RE),
  key: z.string().regex(FIELD_KEY_RE),
  label: LabelText,
  help: z.string().max(500).default(''),
  required: z.boolean().default(false),
  showIf: FieldConditionSchema.nullable().default(null),
  /** Hidden from the page (answers kept and exported) — the safe way to "remove" an answered field. */
  hidden: z.boolean().default(false),
  /** Marked sensitive by the owner (in addition to the automatic label check). */
  sensitive: z.boolean().default(false),
};

export const FieldOptionSchema = z.object({
  id: z.string().regex(OPTION_ID_RE),
  label: z.string().trim().min(1).max(120),
});
export type FieldOption = z.infer<typeof FieldOptionSchema>;

const OptionsSchema = z.array(FieldOptionSchema).min(2).max(MAX_FIELD_OPTIONS);

const YmdOrToday = z.union([z.iso.date(), z.literal('today')]);

export const InterestFieldSchema = z.object({
  ...fieldBase,
  type: z.literal('interest'),
  labels: z
    .object({ yes: OptionalLabel, no: OptionalLabel, maybe: OptionalLabel })
    .default({ yes: '', no: '', maybe: '' }),
  allowMaybe: z.boolean().default(true),
});
export const RsvpFieldSchema = z.object({
  ...fieldBase,
  type: z.literal('rsvp'),
  /** Labels only: maybe / plus-ones are configured in the event details (form_events). */
  labels: z
    .object({
      going: OptionalLabel,
      maybe: OptionalLabel,
      notGoing: OptionalLabel,
      guests: OptionalLabel,
    })
    .default({ going: '', maybe: '', notGoing: '', guests: '' }),
});
export const SingleChoiceFieldSchema = z.object({
  ...fieldBase,
  type: z.literal('single_choice'),
  options: OptionsSchema,
  display: z.enum(['radio', 'select', 'chips']).default('radio'),
  allowOther: z.boolean().default(false),
});
export const MultiChoiceFieldSchema = z.object({
  ...fieldBase,
  type: z.literal('multi_choice'),
  options: OptionsSchema,
  minSelected: z.number().int().min(0).max(MAX_FIELD_OPTIONS).default(0),
  maxSelected: z.number().int().min(1).max(MAX_FIELD_OPTIONS).nullable().default(null),
});
export const RatingFieldSchema = z.object({
  ...fieldBase,
  type: z.literal('rating'),
  scale: z.enum(['stars5', 'nps10']).default('stars5'),
  lowLabel: OptionalLabel,
  highLabel: OptionalLabel,
});
export const ShortTextFieldSchema = z.object({
  ...fieldBase,
  type: z.literal('short_text'),
  maxLength: z.number().int().min(1).max(200).default(200),
  placeholder: z.string().max(120).default(''),
});
export const LongTextFieldSchema = z.object({
  ...fieldBase,
  type: z.literal('long_text'),
  maxLength: z.number().int().min(1).max(2000).default(1000),
  placeholder: z.string().max(200).default(''),
});
export const NumberFieldSchema = z.object({
  ...fieldBase,
  type: z.literal('number'),
  min: z.number().nullable().default(null),
  max: z.number().nullable().default(null),
  integer: z.boolean().default(false),
  unit: z.string().max(20).default(''),
});
export const DateFieldSchema = z.object({
  ...fieldBase,
  type: z.literal('date'),
  min: YmdOrToday.nullable().default(null),
  max: YmdOrToday.nullable().default(null),
});
export const ConsentFieldSchema = z.object({
  ...fieldBase,
  type: z.literal('consent'),
  purpose: z.enum(CONSENT_FIELD_PURPOSES),
  /** Verbatim statement shown next to the box; '' = the system statement for the purpose. */
  statement: z.string().max(1000).default(''),
});
export const CrmFieldSchema = z.object({
  ...fieldBase,
  type: z.literal('crm'),
  target: z.string().refine(isCrmTarget, { message: 'invalid_crm_target' }),
  rule: z.enum(CRM_RULES).default('if_missing'),
  prefill: z.boolean().default(false),
  writeMode: z.enum(CRM_WRITE_MODES).default('fill_empty'),
});

export const FormFieldSchema = z.discriminatedUnion('type', [
  InterestFieldSchema,
  RsvpFieldSchema,
  SingleChoiceFieldSchema,
  MultiChoiceFieldSchema,
  RatingFieldSchema,
  ShortTextFieldSchema,
  LongTextFieldSchema,
  NumberFieldSchema,
  DateFieldSchema,
  ConsentFieldSchema,
  CrmFieldSchema,
]);
export type FormField = z.infer<typeof FormFieldSchema>;
export type FormFieldInput = z.input<typeof FormFieldSchema>;
export type CrmField = z.infer<typeof CrmFieldSchema>;
export type ConsentField = z.infer<typeof ConsentFieldSchema>;

// ─── page (blocks + theme) ───────────────────────────────────────────────────

const Hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, { message: 'invalid_color' });
const BlockId = z.string().regex(BLOCK_ID_RE);
const Align = z.enum(['left', 'center', 'right']);
/** https images only (uploads are served from the API's public origin), '' = not set. */
const ImageSrc = z
  .string()
  .max(2000)
  .refine((u) => u === '' || /^https:\/\/[^\s<>"'{}]+$/i.test(u), { message: 'invalid_image_url' });
const PageText = z.string().max(3000);

export const FORM_PAGE_LAYOUTS = ['card', 'split'] as const;

/** Defaults = GrowHero A3 (blue on cool stone, Commissioner); the tenant's brand colour wins. */
export const FormThemeSchema = z.object({
  brandColor: Hex.default(EMAIL_PALETTE.brand),
  backgroundColor: Hex.default(EMAIL_PALETTE.background),
  contentColor: Hex.default(EMAIL_PALETTE.content),
  textColor: Hex.default(EMAIL_PALETTE.ink),
  font: z.enum(EMAIL_FONTS).default('sans'),
  headingFont: z.enum(EMAIL_FONTS).default('sans'),
  radius: z.number().int().min(0).max(24).default(10),
  layout: z.enum(FORM_PAGE_LAYOUTS).default('card'),
});
export type FormTheme = z.infer<typeof FormThemeSchema>;

const ColumnSide = z.object({
  image: ImageSrc.default(''),
  title: z.string().max(120).default(''),
  text: PageText.default(''),
});

export const FormBlockSchema = z.discriminatedUnion('type', [
  z.object({
    id: BlockId,
    type: z.literal('logo'),
    src: ImageSrc.nullable().default(null),
    width: z.number().int().min(40).max(280).default(120),
  }),
  z.object({
    id: BlockId,
    type: z.literal('hero'),
    title: z.string().max(120).default(''),
    subtitle: z.string().max(300).default(''),
    image: ImageSrc.default(''),
    imageAlt: z.string().max(200).default(''),
    align: Align.default('left'),
    overlay: z.enum(['none', 'soft', 'strong']).default('soft'),
  }),
  z.object({
    id: BlockId,
    type: z.literal('heading'),
    text: z.string().max(200),
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
    align: Align.default('left'),
  }),
  z.object({
    id: BlockId,
    type: z.literal('paragraph'),
    text: PageText,
    align: Align.default('left'),
    size: z.enum(['sm', 'md', 'lg']).default('md'),
  }),
  z.object({
    id: BlockId,
    type: z.literal('image'),
    src: ImageSrc,
    alt: z.string().max(200).default(''),
    width: z.number().int().min(10).max(100).default(100),
    rounded: z.boolean().default(true),
  }),
  z.object({
    id: BlockId,
    type: z.literal('callout'),
    title: z.string().max(120).default(''),
    text: PageText.default(''),
    tone: z.enum(['brand', 'accent', 'soft']).default('soft'),
  }),
  z.object({
    id: BlockId,
    type: z.literal('event_details'),
    showMap: z.boolean().default(true),
    showCalendar: z.boolean().default(true),
    showCapacity: z.boolean().default(false),
  }),
  z.object({
    id: BlockId,
    type: z.literal('columns'),
    left: ColumnSide.default({ image: '', title: '', text: '' }),
    right: ColumnSide.default({ image: '', title: '', text: '' }),
    stackOnPhone: z.boolean().default(true),
  }),
  z.object({
    id: BlockId,
    type: z.literal('faq'),
    items: z
      .array(z.object({ q: z.string().trim().min(1).max(200), a: z.string().max(1000) }))
      .max(10)
      .default([]),
  }),
  z.object({
    id: BlockId,
    type: z.literal('gallery'),
    images: z
      .array(z.object({ src: ImageSrc, alt: z.string().max(200).default('') }))
      .min(2)
      .max(6),
    columns: z.union([z.literal(2), z.literal(3)]).default(3),
  }),
  z.object({
    id: BlockId,
    type: z.literal('countdown'),
    label: z.string().max(80).default(''),
  }),
  z.object({ id: BlockId, type: z.literal('divider') }),
  z.object({
    id: BlockId,
    type: z.literal('spacer'),
    height: z.number().int().min(4).max(96).default(24),
  }),
]);
export type FormBlock = z.infer<typeof FormBlockSchema>;
export type FormBlockType = FormBlock['type'];

export const FormPageSchema = z.object({
  theme: FormThemeSchema.default(FormThemeSchema.parse({})),
  blocks: z.array(FormBlockSchema).max(MAX_PAGE_BLOCKS).default([]),
  footerBlocks: z.array(FormBlockSchema).max(MAX_FOOTER_BLOCKS).default([]),
});
export type FormPage = z.infer<typeof FormPageSchema>;

export const FormTextsSchema = z.object({
  submitLabel: z.string().max(60).default(''),
  /** Thank-you for positive answers (interested/going/maybe). */
  thanksPositive: z.string().max(400).default(''),
  /** Thank-you for negative answers (not interested / not going). */
  thanksNegative: z.string().max(400).default(''),
  closedText: z.string().max(400).default(''),
  fullText: z.string().max(400).default(''),
});
export type FormTexts = z.infer<typeof FormTextsSchema>;

// ─── definition ──────────────────────────────────────────────────────────────

export interface DefinitionIssue {
  code: string;
  path: (string | number)[];
  fieldId?: string;
}

/** Structural rules of a definition for a given kind (also used by the schema's superRefine). */
export function definitionStructureIssues(
  fields: readonly FormField[],
  kind?: FormKind,
): DefinitionIssue[] {
  const issues: DefinitionIssue[] = [];
  const ids = new Set<string>();
  const keys = new Set<string>();
  const targets = new Set<string>();
  const primaries = fields.filter((f) => f.type === 'interest' || f.type === 'rsvp');
  if (primaries.length !== 1) issues.push({ code: 'primary_field_count', path: ['fields'] });
  if (kind && primaries.length === 1) {
    const want = kind === 'interest' ? 'interest' : 'rsvp';
    if (primaries[0]!.type !== want)
      issues.push({ code: 'primary_field_kind', path: ['fields'], fieldId: primaries[0]!.id });
  }
  const seen: string[] = [];
  fields.forEach((f, i) => {
    if (ids.has(f.id)) issues.push({ code: 'duplicate_field_id', path: ['fields', i, 'id'] });
    if (keys.has(f.key)) issues.push({ code: 'duplicate_field_key', path: ['fields', i, 'key'] });
    ids.add(f.id);
    keys.add(f.key);
    if ((f.type === 'interest' || f.type === 'rsvp') && (f.showIf || f.hidden))
      issues.push({ code: 'primary_field_conditional', path: ['fields', i], fieldId: f.id });
    if (f.showIf && !seen.includes(f.showIf.fieldId))
      issues.push({ code: 'condition_not_earlier', path: ['fields', i, 'showIf'], fieldId: f.id });
    if (f.type === 'single_choice' || f.type === 'multi_choice') {
      const optIds = new Set<string>();
      f.options.forEach((o, j) => {
        if (optIds.has(o.id))
          issues.push({ code: 'duplicate_option_id', path: ['fields', i, 'options', j, 'id'] });
        optIds.add(o.id);
      });
      if (
        f.type === 'multi_choice' &&
        f.maxSelected !== null &&
        (f.maxSelected < Math.max(1, f.minSelected) || f.maxSelected > f.options.length)
      )
        issues.push({ code: 'invalid_selection_limits', path: ['fields', i], fieldId: f.id });
    }
    if (f.type === 'number' && f.min !== null && f.max !== null && f.min > f.max)
      issues.push({ code: 'invalid_range', path: ['fields', i], fieldId: f.id });
    if (
      f.type === 'consent' &&
      f.required &&
      (f.purpose === 'marketing_email' || f.purpose === 'marketing_sms')
    )
      // Consent must be freely given: a marketing box can never be mandatory.
      issues.push({ code: 'marketing_consent_required', path: ['fields', i], fieldId: f.id });
    if (f.type === 'crm') {
      if (targets.has(f.target))
        issues.push({ code: 'duplicate_crm_target', path: ['fields', i], fieldId: f.id });
      targets.add(f.target);
      if (f.writeMode === 'overwrite' && (f.target === 'email' || f.target === 'phone'))
        issues.push({ code: 'overwrite_forbidden', path: ['fields', i], fieldId: f.id });
      if (f.rule === 'if_missing_or_unreachable' && f.target !== 'email' && f.target !== 'phone')
        issues.push({ code: 'unreachable_rule_target', path: ['fields', i], fieldId: f.id });
    }
    seen.push(f.id);
  });
  if (targets.has('full_name') && (targets.has('first_name') || targets.has('last_name')))
    issues.push({ code: 'full_name_exclusive', path: ['fields'] });
  return issues;
}

export const FormDefinitionSchema = z
  .object({
    version: z.literal(FORM_DEFINITION_VERSION).default(FORM_DEFINITION_VERSION),
    fields: z.array(FormFieldSchema).max(MAX_FORM_FIELDS),
    page: FormPageSchema.default(FormPageSchema.parse({})),
    texts: FormTextsSchema.default(FormTextsSchema.parse({})),
  })
  .superRefine((def, ctx) => {
    for (const i of definitionStructureIssues(def.fields))
      ctx.addIssue({ code: 'custom', path: i.path, message: i.code });
  });
export type FormDefinition = z.infer<typeof FormDefinitionSchema>;
export type FormDefinitionInput = z.input<typeof FormDefinitionSchema>;

/** Parse a stored definition (null when invalid). */
export function parseFormDefinition(raw: unknown): FormDefinition | null {
  const r = FormDefinitionSchema.safeParse(raw);
  return r.success ? r.data : null;
}

export function primaryField(def: Pick<FormDefinition, 'fields'>): FormField | undefined {
  return def.fields.find((f) => f.type === 'interest' || f.type === 'rsvp');
}

// ─── settings ────────────────────────────────────────────────────────────────

const HttpsUrl = z
  .string()
  .max(500)
  .refine((u) => /^https:\/\/[^\s<>"']+$/i.test(u) && isAllowedHref(u), {
    message: 'invalid_url',
  });

export const FormSettingsSchema = z.object({
  /** The respondent may change the answer (until the deadline / close). */
  allowChanges: z.boolean().default(true),
  share: z
    .object({
      identity: z.enum(SHARE_IDENTITY_MODES).default('email_or_phone'),
      maxPerDay: z.number().int().min(1).max(5000).default(500),
      /** Events: share-link "going" waits for approval. null = on when the event has a capacity. */
      approveSeats: z.boolean().nullable().default(null),
    })
    .default({ identity: 'email_or_phone', maxPerDay: 500, approveSeats: null }),
  /** Tags added to the contact per status (≤ 5 tags each). */
  tagsOnResponse: z
    .partialRecord(z.enum(FORM_RESPONSE_STATUSES), z.array(z.string().trim().min(1).max(40)).max(5))
    .default({}),
  privacyUrl: HttpsUrl.nullable().default(null),
  notifyOwner: z.enum(['off', 'daily']).default('off'),
  retention: z
    .object({ deleteAnswersAfterDays: z.number().int().min(30).max(1095).nullable().default(null) })
    .default({ deleteAnswersAfterDays: null }),
});
export type FormSettings = z.infer<typeof FormSettingsSchema>;

export function parseFormSettings(raw: unknown): FormSettings {
  const r = FormSettingsSchema.safeParse(raw ?? {});
  return r.success ? r.data : FormSettingsSchema.parse({});
}

// ─── event details ───────────────────────────────────────────────────────────

export const FormEventInputSchema = z
  .object({
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }).nullable().default(null),
    locationName: z.string().trim().max(200).default(''),
    address: z.string().trim().max(300).default(''),
    mapUrl: HttpsUrl.nullable().default(null),
    onlineUrl: HttpsUrl.nullable().default(null),
    capacity: z.number().int().min(1).max(100_000).nullable().default(null),
    maxGuests: z.number().int().min(0).max(MAX_GUESTS).default(0),
    shareMaxGuests: z.number().int().min(0).max(MAX_GUESTS).nullable().default(null),
    allowMaybe: z.boolean().default(true),
    rsvpDeadline: z.iso.datetime({ offset: true }).nullable().default(null),
    /** Full event: `going` answers join a FIFO waitlist; freed seats are offered (§7.2). */
    waitlistEnabled: z.boolean().default(false),
    /** Staff check-in list open (`/events/:id/check-in`). */
    checkinOpen: z.boolean().default(true),
  })
  .superRefine((e, ctx) => {
    if (e.endsAt && new Date(e.endsAt) <= new Date(e.startsAt))
      ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'ends_before_start' });
    if (e.shareMaxGuests !== null && e.shareMaxGuests > e.maxGuests)
      ctx.addIssue({ code: 'custom', path: ['shareMaxGuests'], message: 'above_max_guests' });
  });
export type FormEventInput = z.input<typeof FormEventInputSchema>;
export type FormEventParsed = z.output<typeof FormEventInputSchema>;

/** Default share-link guest cap: min(max_guests, 1). */
export function defaultShareMaxGuests(maxGuests: number): number {
  return Math.min(maxGuests, 1);
}

/** Google Maps search link for an address (no key, no embed). */
export function defaultMapUrl(address: string): string | null {
  const a = address.trim();
  return a ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a)}` : null;
}

// ─── answers ─────────────────────────────────────────────────────────────────

export type FormAnswers = Record<string, unknown>;

/** Primary answer of an RSVP field. */
export const RsvpAnswerSchema = z.object({
  status: z.enum(['going', 'maybe', 'not_going']),
  guests: z.number().int().min(0).max(MAX_GUESTS).default(0),
});

export function isAnswered(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if ('other' in o) return typeof o.other === 'string' && o.other.trim() !== '';
    return Object.keys(o).length > 0;
  }
  return true;
}

export interface VisibilityContext {
  /**
   * Who answers: `personal`/`staff` (identity known — CRM `if_missing` fields follow
   * `missingTargets`), `share`/`anonymous` (identity unknown — every CRM field is shown).
   */
  mode: 'personal' | 'staff' | 'share' | 'anonymous';
  /** CRM targets that are missing for this person (server computed). */
  missingTargets?: ReadonlySet<string>;
  /** CRM targets that are missing OR unreachable (bounced email, landline). */
  unreachableTargets?: ReadonlySet<string>;
  /** CRM targets that cannot be collected any more (eligibility lost, §4.2). */
  unavailableTargets?: ReadonlySet<string>;
  /** Share link only: targets covered by the identity block (email/phone/name). */
  identityTargets?: ReadonlySet<string>;
}

function conditionHolds(
  cond: FieldCondition,
  answer: unknown,
  answered: boolean,
  field: FormField | undefined,
): boolean {
  if (cond.op === 'answered') return answered;
  if (cond.op === 'not_answered') return !answered;
  if (!answered) return cond.op === 'neq';
  const values: string[] = [];
  if (field?.type === 'rsvp') {
    const s = (answer as { status?: unknown } | null)?.status;
    if (typeof s === 'string') values.push(s);
  } else if (Array.isArray(answer)) values.push(...answer.filter((x) => typeof x === 'string'));
  else if (typeof answer === 'string') values.push(answer);
  else if (typeof answer === 'boolean') values.push(answer ? 'true' : 'false');
  const want = Array.isArray(cond.value) ? cond.value : cond.value ? [cond.value] : [];
  switch (cond.op) {
    case 'eq':
      return want.length > 0 && values.includes(want[0]!);
    case 'neq':
      return want.length === 0 || !values.includes(want[0]!);
    case 'in':
      return values.some((v) => want.includes(v));
  }
}

/**
 * The single visibility evaluator (public page AND server): which fields are shown for these
 * answers. Hidden fields are never shown; a condition on a field that is not shown sees it as
 * unanswered (the "earlier field" rule keeps the evaluation one pass, no cycles). CRM fields:
 * unavailable targets are never shown; `always` is shown; `if_missing` is shown to anonymous
 * visitors (identity unknown at render) and to known people only when the value is missing.
 */
export function evaluateVisibility(
  def: Pick<FormDefinition, 'fields'>,
  answers: FormAnswers,
  ctx: VisibilityContext,
): Set<string> {
  const visible = new Set<string>();
  const byId = new Map(def.fields.map((f) => [f.id, f]));
  const known = ctx.mode === 'personal' || ctx.mode === 'staff';
  for (const f of def.fields) {
    if (f.hidden) continue;
    if (f.type === 'crm') {
      if (ctx.unavailableTargets?.has(f.target)) continue;
      if (!known && ctx.identityTargets?.has(f.target)) continue;
      if (known && f.rule !== 'always') {
        const set = f.rule === 'if_missing_or_unreachable' ? ctx.unreachableTargets : null;
        const show = ctx.missingTargets?.has(f.target) || set?.has(f.target);
        if (!show) continue;
      }
    }
    if (f.showIf) {
      const ref = byId.get(f.showIf.fieldId);
      const refVisible = visible.has(f.showIf.fieldId);
      const ans = refVisible ? answers[f.showIf.fieldId] : undefined;
      if (!conditionHolds(f.showIf, ans, refVisible && isAnswered(ans), ref)) continue;
    }
    visible.add(f.id);
  }
  return visible;
}

// ─── "missing" rules for CRM targets ─────────────────────────────────────────

export interface ContactFacts {
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  birthDate: string | null;
  attributes: Record<string, unknown>;
  /** Email bounced / suppressed as invalid or bounced (not an opt-out). */
  emailUnreachable?: boolean;
  /** Phone is a landline (SMS-unreachable) or suppressed as invalid. */
  phoneUnreachable?: boolean;
}

const blank = (v: unknown) =>
  v === null ||
  v === undefined ||
  (typeof v === 'string' && v.trim() === '') ||
  (Array.isArray(v) && v.length === 0);

/**
 * Is this CRM value missing for the contact? The NAME is missing only when first name, last name
 * AND display name are all empty (imports often fill only display_name); the last name alone is
 * missing when it is empty and display_name has no second word.
 */
export function isMissing(c: ContactFacts, target: string): boolean {
  switch (target) {
    case 'full_name':
    case 'first_name':
      return blank(c.firstName) && blank(c.lastName) && blank(c.displayName);
    case 'last_name': {
      if (!blank(c.lastName)) return false;
      const words = (c.displayName ?? '').trim().split(/\s+/).filter(Boolean);
      return words.length < 2;
    }
    case 'email':
      return blank(c.email);
    case 'phone':
      return blank(c.phone);
    case 'birth_date':
      return blank(c.birthDate);
    default: {
      const id = crmFieldDefinitionId(target);
      return id ? blank(c.attributes[id]) : true;
    }
  }
}

/** Missing or unreachable (email/phone only); an opt-out is never "unreachable". */
export function isMissingOrUnreachable(c: ContactFacts, target: string): boolean {
  if (isMissing(c, target)) return true;
  if (target === 'email') return Boolean(c.emailUnreachable);
  if (target === 'phone') return Boolean(c.phoneUnreachable);
  return false;
}

// ─── answer validation (structure; CRM values are normalized by the API) ─────

export interface AnswerValidation {
  /** Cleaned answers of visible non-CRM fields (keyed by field id). */
  answers: FormAnswers;
  /** Raw values of visible CRM fields (keyed by field id), normalized by the API. */
  crm: Record<string, unknown>;
  /** Primary status + guests (from the primary field). */
  status: FormResponseStatus | null;
  guests: number;
  /** Field errors keyed by field id: `required`, `invalid`, `too_long`, … */
  errors: Record<string, string>;
}

function cleanString(v: unknown, max: number): string | null | 'too_long' {
  if (typeof v !== 'string') return null;
  // Control characters (except newlines/tabs) are stripped from answers.
  const s = v.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '').trim();
  if (s.length > max) return 'too_long';
  return s;
}

function todayYmd(now: Date): string {
  return now.toISOString().slice(0, 10);
}

/**
 * Validate submitted answers against the definition: only visible fields are kept (hidden or
 * not-shown fields' values are dropped); required visible fields must be answered.
 */
export function validateAnswers(
  def: Pick<FormDefinition, 'fields'>,
  raw: FormAnswers,
  visible: ReadonlySet<string>,
  opts: {
    kind: FormKind;
    allowMaybe?: boolean;
    maxGuests?: number;
    now?: Date;
  },
): AnswerValidation {
  const out: AnswerValidation = { answers: {}, crm: {}, status: null, guests: 0, errors: {} };
  const now = opts.now ?? new Date();
  for (const f of def.fields) {
    if (!visible.has(f.id)) continue;
    const v = raw[f.id];
    const answered = isAnswered(v);
    // CRM fields: `required` means "required when shown" (only visible fields get here).
    const required = f.required || f.type === 'interest' || f.type === 'rsvp';
    if (!answered) {
      if (required) out.errors[f.id] = 'required';
      continue;
    }
    const bad = (code = 'invalid') => {
      out.errors[f.id] = code;
    };
    switch (f.type) {
      case 'interest': {
        if (v !== 'interested' && v !== 'not_interested' && v !== 'maybe') bad();
        else if (v === 'maybe' && !f.allowMaybe) bad();
        else out.status = v;
        break;
      }
      case 'rsvp': {
        const r = RsvpAnswerSchema.safeParse(v);
        if (!r.success) bad();
        else if (r.data.status === 'maybe' && opts.allowMaybe === false) bad();
        else {
          const guests = r.data.status === 'not_going' ? 0 : r.data.guests;
          if (guests > (opts.maxGuests ?? 0)) bad('too_many_guests');
          else {
            out.status = r.data.status;
            out.guests = guests;
            out.answers[f.id] = { status: r.data.status, guests };
          }
        }
        break;
      }
      case 'single_choice': {
        if (typeof v === 'string' && f.options.some((o) => o.id === v)) out.answers[f.id] = v;
        else if (f.allowOther && v && typeof v === 'object' && 'other' in v) {
          const s = cleanString((v as { other: unknown }).other, 200);
          if (s === 'too_long') bad('too_long');
          else if (!s) bad();
          else out.answers[f.id] = { other: s };
        } else bad();
        break;
      }
      case 'multi_choice': {
        if (!Array.isArray(v) || v.some((x) => typeof x !== 'string')) {
          bad();
          break;
        }
        const ids = [...new Set(v as string[])];
        if (ids.some((id) => !f.options.some((o) => o.id === id))) bad();
        else if (ids.length < f.minSelected) bad('too_few');
        else if (f.maxSelected !== null && ids.length > f.maxSelected) bad('too_many');
        else out.answers[f.id] = ids;
        break;
      }
      case 'rating': {
        const max = f.scale === 'stars5' ? 5 : 10;
        const min = f.scale === 'stars5' ? 1 : 0;
        if (typeof v !== 'number' || !Number.isInteger(v) || v < min || v > max) bad();
        else out.answers[f.id] = v;
        break;
      }
      case 'short_text':
      case 'long_text': {
        const s = cleanString(v, f.maxLength);
        if (s === 'too_long') bad('too_long');
        else if (s === null) bad();
        else if (s === '') {
          if (f.required) out.errors[f.id] = 'required';
        } else out.answers[f.id] = s;
        break;
      }
      case 'number': {
        if (typeof v !== 'number' || !Number.isFinite(v)) bad();
        else if (f.integer && !Number.isInteger(v)) bad();
        else if ((f.min !== null && v < f.min) || (f.max !== null && v > f.max))
          bad('out_of_range');
        else out.answers[f.id] = v;
        break;
      }
      case 'date': {
        if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v) || !isRealDate(v)) {
          bad();
          break;
        }
        const min = f.min === 'today' ? todayYmd(now) : f.min;
        const max = f.max === 'today' ? todayYmd(now) : f.max;
        if ((min && v < min) || (max && v > max)) bad('out_of_range');
        else out.answers[f.id] = v;
        break;
      }
      case 'consent': {
        if (typeof v !== 'boolean') bad();
        else if (v) out.answers[f.id] = true;
        else if (f.required) out.errors[f.id] = 'required';
        break;
      }
      case 'crm': {
        // Normalized/validated per target by the API (phone parsing, custom field types).
        if (typeof v === 'string') {
          const s = cleanString(v, 5000);
          if (s === 'too_long') bad('too_long');
          else if (s) out.crm[f.id] = s;
          else if (f.required) out.errors[f.id] = 'required';
        } else if (typeof v === 'number' || typeof v === 'boolean' || Array.isArray(v))
          out.crm[f.id] = v;
        else bad();
        break;
      }
    }
  }
  return out;
}

export function isRealDate(ymd: string): boolean {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

// ─── compatibility (editing after answers exist, version races) ──────────────

export interface CompatibilityIssue {
  code:
    | 'field_type_changed'
    | 'field_key_changed'
    | 'crm_target_changed'
    | 'option_removed'
    | 'field_removed'
    | 'primary_removed';
  fieldId: string;
  optionId?: string;
}

export interface AnsweredSet {
  fieldIds: ReadonlySet<string>;
  /** `${fieldId}:${optionId}` pairs that have answers. */
  optionKeys: ReadonlySet<string>;
}

/**
 * Changes that would break existing answers (§5.5): a field's type, key or CRM target changing,
 * an answered option or field removed (rename or hide instead), the primary field removed.
 * `answered = null` checks structure only (version races: every previous field must survive).
 */
export function assertCompatible(
  prev: Pick<FormDefinition, 'fields'>,
  next: Pick<FormDefinition, 'fields'>,
  answered: AnsweredSet | null,
): CompatibilityIssue[] {
  const issues: CompatibilityIssue[] = [];
  const nextById = new Map(next.fields.map((f) => [f.id, f]));
  for (const p of prev.fields) {
    const n = nextById.get(p.id);
    const isPrimary = p.type === 'interest' || p.type === 'rsvp';
    if (!n) {
      if (isPrimary) issues.push({ code: 'primary_removed', fieldId: p.id });
      else if (!answered || answered.fieldIds.has(p.id))
        issues.push({ code: 'field_removed', fieldId: p.id });
      continue;
    }
    if (n.type !== p.type) {
      issues.push({ code: 'field_type_changed', fieldId: p.id });
      continue;
    }
    if (n.key !== p.key) issues.push({ code: 'field_key_changed', fieldId: p.id });
    if (p.type === 'crm' && n.type === 'crm' && n.target !== p.target)
      issues.push({ code: 'crm_target_changed', fieldId: p.id });
    if (
      (p.type === 'single_choice' || p.type === 'multi_choice') &&
      (n.type === 'single_choice' || n.type === 'multi_choice')
    ) {
      const kept = new Set(n.options.map((o) => o.id));
      for (const o of p.options)
        if (!kept.has(o.id) && (!answered || answered.optionKeys.has(`${p.id}:${o.id}`)))
          issues.push({ code: 'option_removed', fieldId: p.id, optionId: o.id });
    }
  }
  return issues;
}

export interface RenameWarning {
  fieldId: string;
  optionId?: string;
  from: string;
  to: string;
}

/** Labels (field or option) that changed although they already have answers (publish dialog). */
export function renameWarnings(
  prev: Pick<FormDefinition, 'fields'>,
  next: Pick<FormDefinition, 'fields'>,
  answered: AnsweredSet,
): RenameWarning[] {
  const out: RenameWarning[] = [];
  const nextById = new Map(next.fields.map((f) => [f.id, f]));
  for (const p of prev.fields) {
    const n = nextById.get(p.id);
    if (!n || !answered.fieldIds.has(p.id)) continue;
    if (n.label !== p.label) out.push({ fieldId: p.id, from: p.label, to: n.label });
    if (
      (p.type === 'single_choice' || p.type === 'multi_choice') &&
      (n.type === 'single_choice' || n.type === 'multi_choice')
    )
      for (const o of p.options) {
        const no = n.options.find((x) => x.id === o.id);
        if (no && no.label !== o.label && answered.optionKeys.has(`${p.id}:${o.id}`))
          out.push({ fieldId: p.id, optionId: o.id, from: o.label, to: no.label });
      }
  }
  return out;
}

/**
 * Version race (§5.5): an answer typed under `submitted` may be stored when the change to
 * `current` is compatible and every submitted field/option still exists in `current`.
 */
export function submissionCompatible(
  submitted: Pick<FormDefinition, 'fields'>,
  current: Pick<FormDefinition, 'fields'>,
  answers: FormAnswers,
): boolean {
  if (assertCompatible(submitted, current, null).length > 0) return false;
  const cur = new Map(current.fields.map((f) => [f.id, f]));
  for (const [id, v] of Object.entries(answers)) {
    const f = cur.get(id);
    if (!f) return false;
    if (f.type === 'single_choice' && typeof v === 'string' && !f.options.some((o) => o.id === v))
      return false;
    if (
      f.type === 'multi_choice' &&
      Array.isArray(v) &&
      v.some((x) => !f.options.some((o) => o.id === x))
    )
      return false;
  }
  return true;
}

// ─── sensitivity, readiness ──────────────────────────────────────────────────

/** A field asks for special-category data (label check or the owner's manual mark). */
export function looksSensitiveField(f: FormField): boolean {
  if (f.sensitive) return true;
  if (f.type === 'consent' || f.type === 'interest' || f.type === 'rsvp') return false;
  const options =
    f.type === 'single_choice' || f.type === 'multi_choice' ? f.options.map((o) => o.label) : [];
  return looksSensitiveLabel(f.label, f.help, ...options);
}

export interface FormIssue {
  code:
    | 'definition_invalid'
    | 'primary_field_missing'
    | 'empty_option_label'
    | 'special_category_consent_missing'
    | 'consent_statement_missing'
    | 'event_details_missing'
    | 'event_in_past'
    | 'deadline_after_start'
    | 'closes_in_past'
    | 'crm_target_unavailable'
    | 'privacy_controller_missing'
    | 'needs_attention'
    | 'too_many_fields'
    | 'marketing_consent_missing';
  severity: 'error' | 'warning';
  fieldId?: string;
}

/**
 * Readiness checklist (publish step): errors block publishing, warnings are advisory.
 * The caller provides what it knows about the tenant/event (all optional for the builder).
 */
export function formIssues(
  kind: FormKind,
  def: FormDefinition,
  ctx: {
    now: Date;
    event?: { startsAt: string | null; rsvpDeadline: string | null } | null;
    closesAt?: string | null;
    unavailableTargets?: ReadonlySet<string>;
    privacyReady?: boolean;
    needsAttention?: { code: string; fieldId?: string }[];
  },
): FormIssue[] {
  const issues: FormIssue[] = [];
  for (const i of definitionStructureIssues(def.fields, kind))
    issues.push({
      code: i.code === 'primary_field_count' ? 'primary_field_missing' : 'definition_invalid',
      severity: 'error',
      fieldId: i.fieldId,
    });
  const sensitive = def.fields.some((f) => !f.hidden && looksSensitiveField(f));
  if (
    sensitive &&
    !def.fields.some(
      (f) => f.type === 'consent' && f.purpose === 'special_category' && f.required && !f.hidden,
    )
  )
    issues.push({ code: 'special_category_consent_missing', severity: 'error' });
  for (const f of def.fields) {
    if (f.type === 'consent' && f.purpose === 'custom' && !f.statement.trim())
      issues.push({ code: 'consent_statement_missing', severity: 'error', fieldId: f.id });
    if (f.type === 'crm' && ctx.unavailableTargets?.has(f.target) && !f.hidden)
      issues.push({ code: 'crm_target_unavailable', severity: 'error', fieldId: f.id });
  }
  if (kind === 'event') {
    const e = ctx.event;
    if (!e?.startsAt) issues.push({ code: 'event_details_missing', severity: 'error' });
    else {
      if (new Date(e.startsAt) <= ctx.now)
        issues.push({ code: 'event_in_past', severity: 'error' });
      if (e.rsvpDeadline && new Date(e.rsvpDeadline) > new Date(e.startsAt))
        issues.push({ code: 'deadline_after_start', severity: 'error' });
    }
  }
  if (ctx.closesAt && new Date(ctx.closesAt) <= ctx.now)
    issues.push({ code: 'closes_in_past', severity: 'error' });
  if (ctx.privacyReady === false)
    issues.push({ code: 'privacy_controller_missing', severity: 'error' });
  for (const n of ctx.needsAttention ?? [])
    issues.push({ code: 'needs_attention', severity: 'error', fieldId: n.fieldId });
  if (def.fields.filter((f) => !f.hidden).length > FORM_FIELDS_WARNING)
    issues.push({ code: 'too_many_fields', severity: 'warning' });
  if (
    kind === 'interest' &&
    !def.fields.some((f) => f.type === 'consent' && f.purpose === 'marketing_email')
  )
    issues.push({ code: 'marketing_consent_missing', severity: 'warning' });
  return issues;
}

// ─── system copy (consent statements, default labels) ────────────────────────

type L = 'el' | 'en';

/** Verbatim system consent statements (legal text is never AI-written). */
export const CONSENT_STATEMENTS: Record<
  Exclude<ConsentFieldPurpose, 'custom'>,
  Record<L, string>
> = {
  marketing_email: {
    el: 'Θέλω να λαμβάνω ενημερώσεις και προσφορές με email από την επιχείρηση. Μπορώ να διαγραφώ οποτεδήποτε.',
    en: 'I want to receive news and offers by email from the business. I can unsubscribe at any time.',
  },
  marketing_sms: {
    el: 'Θέλω να λαμβάνω ενημερώσεις και προσφορές με SMS από την επιχείρηση. Μπορώ να διαγραφώ οποτεδήποτε.',
    en: 'I want to receive news and offers by SMS from the business. I can unsubscribe at any time.',
  },
  special_category: {
    el: 'Συναινώ ρητά στην επεξεργασία των στοιχείων υγείας που δίνω (π.χ. αλλεργίες) αποκλειστικά για την οργάνωση αυτής της εκδήλωσης/υπηρεσίας. Μπορώ να ανακαλέσω τη συναίνεσή μου οποτεδήποτε.',
    en: 'I explicitly consent to the processing of the health information I provide (e.g. allergies) solely to organise this event/service. I can withdraw my consent at any time.',
  },
};

export function consentStatement(f: ConsentField, locale: L): string {
  if (f.statement.trim()) return f.statement.trim();
  if (f.purpose === 'custom') return '';
  return CONSENT_STATEMENTS[f.purpose][locale];
}

export const DEFAULT_PRIMARY_LABELS: Record<L, Record<string, string>> = {
  el: {
    interested: 'Ναι, με ενδιαφέρει',
    not_interested: 'Όχι, ευχαριστώ',
    maybe: 'Ίσως',
    going: 'Θα έρθω',
    not_going: 'Δεν θα έρθω',
    guests: 'Συνοδοί',
  },
  en: {
    interested: "Yes, I'm interested",
    not_interested: 'No, thank you',
    maybe: 'Maybe',
    going: "I'll be there",
    not_going: "I can't make it",
    guests: 'Guests',
  },
};

/** Label a person sees for a primary status (owner label, else the system default). */
export function primaryOptionLabel(
  f: FormField | undefined,
  status: FormResponseStatus | 'guests',
  locale: L,
): string {
  const d = DEFAULT_PRIMARY_LABELS[locale][status] ?? status;
  if (f?.type === 'interest') {
    const l =
      status === 'interested'
        ? f.labels.yes
        : status === 'not_interested'
          ? f.labels.no
          : status === 'maybe'
            ? f.labels.maybe
            : '';
    return l || d;
  }
  if (f?.type === 'rsvp') {
    const l =
      status === 'going'
        ? f.labels.going
        : status === 'not_going'
          ? f.labels.notGoing
          : status === 'maybe'
            ? f.labels.maybe
            : status === 'guests'
              ? f.labels.guests
              : '';
    return l || d;
  }
  return d;
}

/** Owner-facing status labels (dashboards, CSV, Greek formal). */
export const FORM_STATUS_LABELS: Record<L, Record<FormResponseStatus | 'no_reply', string>> = {
  el: {
    interested: 'Ενδιαφέρεται',
    maybe: 'Ίσως',
    not_interested: 'Δεν ενδιαφέρεται',
    going: 'Έρχεται',
    not_going: 'Δεν έρχεται',
    waitlist: 'Λίστα αναμονής',
    no_reply: 'Χωρίς απάντηση',
  },
  en: {
    interested: 'Interested',
    maybe: 'Maybe',
    not_interested: 'Not interested',
    going: 'Going',
    not_going: 'Not going',
    waitlist: 'Waitlist',
    no_reply: 'No reply',
  },
};

export const CRM_TARGET_LABELS: Record<L, Record<CrmCoreTarget, string>> = {
  el: {
    full_name: 'Ονοματεπώνυμο',
    first_name: 'Όνομα',
    last_name: 'Επώνυμο',
    email: 'Email',
    phone: 'Κινητό τηλέφωνο',
    birth_date: 'Ημερομηνία γέννησης',
  },
  en: {
    full_name: 'Full name',
    first_name: 'First name',
    last_name: 'Last name',
    email: 'Email',
    phone: 'Mobile phone',
    birth_date: 'Date of birth',
  },
};

// ─── defaults ────────────────────────────────────────────────────────────────

/** Default page theme (ADR-EMAIL-4: tenant brand colour, else laurel). */
export function defaultFormTheme(brandColor?: string | null): FormTheme {
  return FormThemeSchema.parse(
    brandColor && /^#[0-9a-fA-F]{6}$/.test(brandColor) ? { brandColor } : {},
  );
}

/** A minimal valid definition for a kind (primary field + event card for events). */
export function defaultFormDefinition(
  kind: FormKind,
  opts: { locale?: L; brandColor?: string | null; title?: string } = {},
): FormDefinition {
  const locale = opts.locale ?? 'el';
  const primary: FormField =
    kind === 'interest'
      ? InterestFieldSchema.parse({
          id: newFieldId(),
          key: 'interest',
          type: 'interest',
          label: locale === 'el' ? 'Σας ενδιαφέρει;' : 'Are you interested?',
          required: true,
        })
      : RsvpFieldSchema.parse({
          id: newFieldId(),
          key: 'rsvp',
          type: 'rsvp',
          label: locale === 'el' ? 'Θα έρθετε;' : 'Will you come?',
          required: true,
        });
  const blocks: FormBlock[] = [
    FormBlockSchema.parse({
      id: 'hero',
      type: 'hero',
      title: opts.title ?? '',
      subtitle: '',
    }),
  ];
  if (kind === 'event') blocks.push(FormBlockSchema.parse({ id: 'event', type: 'event_details' }));
  return FormDefinitionSchema.parse({
    fields: [primary],
    page: { theme: defaultFormTheme(opts.brandColor), blocks, footerBlocks: [] },
    texts: {},
  });
}

/** Event forms always carry exactly one `event_details` block (it can be moved, not removed). */
export function ensureEventDetailsBlock(def: FormDefinition, kind: FormKind): FormDefinition {
  const has =
    def.page.blocks.some((b) => b.type === 'event_details') ||
    def.page.footerBlocks.some((b) => b.type === 'event_details');
  if (kind !== 'event') {
    if (!has) return def;
    return {
      ...def,
      page: {
        ...def.page,
        blocks: def.page.blocks.filter((b) => b.type !== 'event_details'),
        footerBlocks: def.page.footerBlocks.filter((b) => b.type !== 'event_details'),
      },
    };
  }
  if (has) return def;
  if (def.page.blocks.length >= MAX_PAGE_BLOCKS) return def;
  const blocks = [...def.page.blocks];
  const at = blocks.findIndex((b) => b.type === 'hero');
  blocks.splice(at >= 0 ? at + 1 : 0, 0, {
    id: 'event',
    type: 'event_details',
    showMap: true,
    showCalendar: true,
    showCapacity: false,
  });
  return { ...def, page: { ...def.page, blocks } };
}

// ─── links ───────────────────────────────────────────────────────────────────

/** Personal link tokens: 16 bytes base64url. */
export const FORM_TOKEN_RE = /^[A-Za-z0-9_-]{22}$/;
/** Share codes: 12 base62 characters. */
export const FORM_SHARE_CODE_RE = /^[A-Za-z0-9]{12}$/;
/** Matches a live personal form link inside rendered message text. */
export const FORM_LINK_IN_TEXT_RE = /\/f\/[A-Za-z0-9_-]{22}(?![A-Za-z0-9_-])/g;
export const MASKED_FORM_LINK = '/f/•••';

/** Replace live `/f/<token>` links with a masked placeholder (staff views, exports). */
export function maskFormLinks(text: string): string;
export function maskFormLinks(text: string | null): string | null;
export function maskFormLinks(text: string | null): string | null {
  return text == null ? text : text.replace(FORM_LINK_IN_TEXT_RE, MASKED_FORM_LINK);
}

/** Is this URL a personal form link (never wrapped in tracked links)? */
export function isFormLinkUrl(url: string): boolean {
  return /\/f\/(preview\/)?[A-Za-z0-9_.-]{22,}/.test(url);
}
